import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a Rwandan invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Rwandan invoice should include: your business name and address; your TIN (Taxpayer Identification Number) if VAT registered; the client name and address; a unique invoice number; the invoice date; a clear description of goods or services; the amount in RWF (Rwandan Franc); and your payment details. VAT-registered businesses must show the VAT rate (18%), VAT amount, and the total inclusive of VAT. VAT receipts must also display a unique RRA-issued serial code.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the VAT rate in Rwanda?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The standard VAT rate in Rwanda is 18%. Businesses must register for VAT with the Rwanda Revenue Authority (RRA) once annual taxable turnover exceeds RWF 20 million. VAT-registered businesses must issue RRA-compliant receipts and file monthly VAT returns. Rwanda has mandatory electronic billing through EBM (Electronic Billing Machine) for VAT-registered taxpayers.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do Rwandan freelancers invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Rwandan freelancers typically invoice overseas clients in USD or EUR. Services exported to foreign clients are zero-rated for VAT (0% VAT), so you do not charge Rwandan VAT on international invoices. Most Rwandan freelancers receive international payments via Payoneer, Wise, or SWIFT bank wire. MTN MoMo also supports some international transfers. Invoice in the currency your client prefers and specify your payment details clearly.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is EBM invoicing in Rwanda?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'EBM (Electronic Billing Machine) is Rwanda\'s mandatory electronic invoicing system for VAT-registered taxpayers. All VAT-registered businesses must issue EBM-generated receipts that carry a unique QR code and serial number from the RRA server. EBM invoices are transmitted to the RRA in real time. Penalties apply for non-compliance. For small businesses and freelancers below the RWF 20 million threshold, standard invoices are sufficient.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this invoice generator free for Rwandan users?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is completely free for Rwandan freelancers and businesses. Create professional invoices in RWF, USD, EUR, or any currency. Download a clean PDF instantly — no sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Percent,
    title: 'VAT Invoice',
    subtitle: 'For VAT-registered Rwandan businesses',
    features: ['18% VAT auto-calculated', 'TIN / VAT number field', 'Net + VAT + total', 'RRA-compliant format', 'RWF currency'],
    to: '/create/vat',
    color: 'green',
    btnClass: 'bg-green-600 hover:bg-green-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For businesses below RWF 20M threshold',
    features: ['No VAT required', 'RWF currency', 'Custom line items', 'Logo upload', 'Payment terms'],
    to: '/create/freelancer',
    color: 'slate',
    btnClass: 'bg-slate-700 hover:bg-slate-800',
  },
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients in USD/EUR',
    features: ['USD, EUR, GBP billing', 'Zero-rated export format', 'SWIFT / Payoneer details', 'Multi-currency support', 'Professional PDF'],
    to: '/create/international',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorRwandaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Rwanda: Free RWF Invoice Maker | 18% VAT | Invilic"
        description="Free invoice generator for Rwandan businesses and freelancers. Create VAT invoices with 18% VAT or standard RWF invoices. PDF download free, no sign-up required."
        canonical="https://www.invilic.com/invoice-generator-rwanda"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇷🇼 Rwanda
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Rwanda
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Rwandan businesses, freelancers, and sole traders. Create VAT invoices with 18% VAT auto-calculated, or standard RWF invoices. Download professional PDF invoices instantly — no sign-up needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/vat" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-green-600/20 transition-all duration-200">
              Create VAT Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-800 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-200">
              Create Standard Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">Rwanda VAT Invoice vs Standard Invoice</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-green-700 dark:text-green-400 mb-3">Use a VAT Invoice if:</div>
              {[
                'Your annual taxable turnover exceeds RWF 20 million',
                'You are VAT registered with the Rwanda Revenue Authority (RRA)',
                'Your client needs a VAT receipt showing the 18% VAT breakdown',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-slate-700 dark:text-slate-400 mb-3">Use a Standard Invoice if:</div>
              {[
                'Your annual turnover is below the RWF 20 million VAT threshold',
                'You are a freelancer or sole trader not yet VAT registered',
                'You are billing an overseas client (exports are typically zero-rated)',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16 p-6 rounded-2xl border border-green-100 dark:border-green-800/40 bg-green-50 dark:bg-green-900/20">
          <h2 className="font-bold text-gray-900 dark:text-white mb-3">Receiving Payments as a Rwandan Freelancer</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Rwanda has one of Africa's most advanced digital payment ecosystems. Here are the main options:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { label: 'MTN MoMo (Mobile Money)', detail: 'The most widely used payment method in Rwanda. Share your MTN MoMo number with local clients for instant RWF transfers. MTN MoMo also supports international transfers from certain countries.' },
              { label: 'Airtel Money', detail: 'Rwanda\'s second-largest mobile money network. Suitable for receiving local RWF payments from businesses and individuals.' },
              { label: 'Payoneer', detail: 'Best option for billing international clients on platforms like Upwork, Toptal, and Fiverr. Withdraw to your Rwandan bank account in RWF.' },
              { label: 'Bank wire (SWIFT)', detail: 'Rwandan banks (Bank of Kigali, Equity Bank Rwanda, I&M Bank Rwanda) accept international SWIFT transfers in USD or EUR. Provide your bank name, SWIFT code, and account number.' },
            ].map(({ label, detail }) => (
              <div key={label} className="p-3 bg-white dark:bg-gray-900/50 rounded-xl border border-green-100 dark:border-green-800/30">
                <p className="font-semibold text-gray-900 dark:text-white text-xs mb-1">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Rwanda Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your Rwandan Invoice for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF for Rwandan businesses in under 2 minutes. No account required.</p>
          <Link to="/create/vat" className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-green-600/20 transition-all duration-200 text-lg">
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
